/** Investor-facing Starlink series for the revenue-map product view.
 * Printed 10-Q / S-1 first. Independent proxies and company reports are labeled.
 * Do not mix these objects into one multiply.
 */

export type InvestorKind = 'printed' | 'independent' | 'company_report' | 'hole';

export type InvestorRow = {
  metric: string;
  value: string;
  source: string;
  kind: InvestorKind;
  group: InvestorGroupId;
};

export type InvestorGroupId =
  | 'pnl'
  | 'units'
  | 'network'
  | 'balance'
  | 'verticals'
  | 'proxies'
  | 'holes';

export type InvestorQuestion = {
  id: string;
  title: string;
  body: string;
};

export type ArpuPoint = {
  label: string;
  arpu: number;
  subscribersM: number | null;
  source: string;
};

export const INVESTOR_AS_OF = 'Q2 2026 10-Q (period ended June 30) · S-1 / 424(b)(4) for FY 2025 and Mar 31';

export const INVESTOR_QUESTIONS: InvestorQuestion[] = [
  {
    id: 'cash',
    title: 'How much does the network earn?',
    body: 'Connectivity revenue, Consumer vs Enterprise & Government vs kits, and Connectivity operating income. Consumer is billed monthly — it is not a pull from backlog.',
  },
  {
    id: 'unit',
    title: 'What is the unit?',
    body: 'Self-serve service lines (not dishes), ARPU on the average count, kit ASP vs manufacturing cost. The EoP × ARPU × months multiply does not close to Consumer.',
  },
  {
    id: 'capacity',
    title: 'Can the network serve the next add?',
    body: 'Operational satellites, markets, Direct-to-cell constellation, and kit landings. Capacity is an envelope, not a Consumer dollar.',
  },
  {
    id: 'break',
    title: 'What can break the mix?',
    body: 'International ARPU dilution, unprinted E&G vertical mix, Starshield timing, spectrum, and the five-year satellite replacement cycle.',
  },
];

export const CONSUMER_IDENTITY = {
  eopM: 12.0,
  arpu: 66,
  months: 3,
  rolledM: 2_376,
  printedConsumerM: 2_485,
  residualM: 109,
  caveat:
    '12.0 million EoP × $66 ARPU × 3 months = $2,376 million. Printed Consumer is $2,485 million. ARPU is defined on the average subscriber count, which this 10-Q does not print. The KPI includes roam and self-serve business. Kits sit in Connectivity products.',
};

export const ARPU_PATH: ArpuPoint[] = [
  { label: 'FY23', arpu: 99, subscribersM: 2.3, source: 'SPCX S-1' },
  { label: 'FY24', arpu: 91, subscribersM: 4.4, source: 'SPCX S-1' },
  { label: 'FY25', arpu: 81, subscribersM: 8.9, source: 'SPCX S-1' },
  { label: "Q1'26", arpu: 66, subscribersM: 10.3, source: 'SPCX S-1 / EX-99.1' },
  { label: "Q2'26", arpu: 66, subscribersM: 12.0, source: 'SPCX 10-Q Key Business Metrics' },
];

export const INVESTOR_ROWS: InvestorRow[] = [
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Connectivity Q2 2026',
    value: '$4,291 million',
    source: 'SPCX 10-Q, Note 18',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Connectivity Q2 2025',
    value: '$2,588 million',
    source: 'SPCX 10-Q, Note 18',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Consumer Q2 2026',
    value: '$2,485 million',
    source: 'SPCX 10-Q, Note 3 · month-to-month bills',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Enterprise & Government Q2 2026',
    value: '$1,806 million',
    source: 'SPCX 10-Q, Note 3 · +108% YoY',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Connectivity products (kits) Q2 2026',
    value: '$461 million',
    source: 'SPCX 10-Q, Note 3 · all Connectivity kits',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'FY 2025 Connectivity revenue',
    value: '$11,387 million',
    source: 'SPCX 424(b)(4) / S-1',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'FY 2025 Connectivity operating income',
    value: '$4,423 million',
    source: 'SPCX S-1 · ~38.8% operating margin',
  },
  {
    group: 'pnl',
    kind: 'printed',
    metric: 'Q1 2026 Connectivity operating income',
    value: '$1,188 million',
    source: 'SPCX S-1',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'Starlink subscribers Jun 30, 2026',
    value: '12.0 million EoP',
    source: 'SPCX 10-Q, Key Business Metrics · unique service lines',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'Starlink subscribers Mar 31, 2026',
    value: '10.3 million EoP',
    source: 'SPCX EX-99.1 / S-1',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'Starlink subscribers Jun 30, 2025',
    value: '6.0 million EoP',
    source: 'SPCX 10-Q, Key Business Metrics',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'ARPU Q2 / H1 2026',
    value: '$66 / month',
    source: 'SPCX 10-Q · defined on average count (unprinted)',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'ARPU Q2 / H1 2025',
    value: '$85 / month',
    source: 'SPCX 10-Q, Key Business Metrics',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'ARPU FY 2023 → FY 2025',
    value: '$99 → $91 → $81',
    source: 'SPCX S-1',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'Average kit manufacturing cost vs 2022',
    value: '−59%',
    source: 'SPCX S-1 · kit average, not Standard 4X CM',
  },
  {
    group: 'units',
    kind: 'printed',
    metric: 'Starlink Mobile monthly unique devices',
    value: '~7.4 million (Mar 31)',
    source: 'SPCX S-1 · distinct from 12.0M broadband KPI',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'Operational satellites Jun 30, 2026',
    value: '10,200+',
    source: 'SPCX 10-Q, MD&A Overview',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'Operational satellites Mar 31, 2026',
    value: '~9,600',
    source: 'SPCX 424(b)(4)',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'V1 Mobile satellites (Mar 31)',
    value: '~650',
    source: 'SPCX S-1',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'Markets (10-Q MD&A)',
    value: '167',
    source: 'SPCX 10-Q · footprint, not Consumer geo mix',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'Direct-to-cell countries (Mar 31)',
    value: '~30',
    source: 'SPCX S-1',
  },
  {
    group: 'network',
    kind: 'printed',
    metric: 'V3 satellite downlink (design)',
    value: '1 Tbps / satellite',
    source: 'SPCX S-1 · Starship deployment 2H 2026 expected',
  },
  {
    group: 'balance',
    kind: 'printed',
    metric: 'Backlog Jun 30, 2026',
    value: '$47,461 million',
    source: 'SPCX 10-Q Note 3 · excludes billed-as-delivered Consumer',
  },
  {
    group: 'balance',
    kind: 'printed',
    metric: 'Deferred revenue Jun 30, 2026',
    value: '$14,286 million',
    source: 'SPCX 10-Q Note 3 · Space + Connectivity E&G',
  },
  {
    group: 'balance',
    kind: 'printed',
    metric: 'Backlog recognized within one year',
    value: '~56%',
    source: 'SPCX 10-Q Note 3 · contracted book, not Consumer',
  },
  {
    group: 'verticals',
    kind: 'printed',
    metric: 'E&G increase walk Q2',
    value: '+$939 million',
    source: 'MD&A names government, aviation, maritime, other enterprise · mix unprinted',
  },
  {
    group: 'verticals',
    kind: 'printed',
    metric: 'Starlink Mobile segment home',
    value: 'Enterprise & Government',
    source: 'SPCX 10-Q Note 3 footnote 1',
  },
  {
    group: 'verticals',
    kind: 'company_report',
    metric: 'Vessels connected (2025 progress report)',
    value: '>150,000',
    source: 'starlinkProgressReport_2025.pdf · not a 10-Q dollar',
  },
  {
    group: 'verticals',
    kind: 'company_report',
    metric: 'Commercial aircraft equipped in 2025',
    value: '>1,400',
    source: 'starlinkProgressReport_2025.pdf',
  },
  {
    group: 'verticals',
    kind: 'company_report',
    metric: 'Business jets equipped',
    value: '>1,000',
    source: 'starlinkProgressReport_2025.pdf',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'Live markets (availability.json)',
    value: 'Warehouse',
    source: 'Official Starlink availability · see markets map',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'Ookla sample mix / p50 speeds',
    value: 'Warehouse',
    source: 'Usage proxy, not a subscriber census',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'Labeled kit BOLs',
    value: 'Warehouse',
    source: 'Public customs lines · Connectivity products landings',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'US HS 851762 corridors',
    value: 'UN Comtrade / OEC / Census',
    source: 'Category-level, not Starlink-only',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'Constellation census',
    value: 'CelesTrak · McDowell',
    source: 'Independent GP catalog vs 10-Q 10,200 print',
  },
  {
    group: 'proxies',
    kind: 'independent',
    metric: 'Government awards',
    value: 'USAspending / SAM',
    source: 'Starshield vs commercial Starlink · not Note 3 E&G mix',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'Average Starlink subscribers',
    value: 'Unprinted',
    source: 'Required to use the ARPU definition',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'Adds / churn / cohorts',
    value: 'Unprinted',
    source: 'Growth quality vs promo junk',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'Consumer geo and plan mix',
    value: 'Unprinted',
    source: '167 markets is a footprint, not dollars',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'Kit units / ASP / CM by SKU',
    value: 'Unprinted',
    source: 'Connectivity products is a dollar, not Standard 4X units',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'E&G vertical dollar split',
    value: 'Unprinted',
    source: 'Maritime vs aviation vs Starshield vs Mobile',
  },
  {
    group: 'holes',
    kind: 'hole',
    metric: 'Capacity utilization / SAC / LTV',
    value: 'Unprinted',
    source: 'Sats × Gbps is not congestion; cheap international adds lack payback',
  },
];

export const INVESTOR_GROUPS: Array<{ id: InvestorGroupId; label: string }> = [
  { id: 'pnl', label: 'Printed P&L' },
  { id: 'units', label: 'Unit economics' },
  { id: 'network', label: 'Network / capacity' },
  { id: 'balance', label: 'Backlog & deferred' },
  { id: 'verticals', label: 'High-ARPU verticals' },
  { id: 'proxies', label: 'Independent checks' },
  { id: 'holes', label: 'Still a hole' },
];

export const INVESTOR_DOCS: Array<{ date: string; title: string; detail: string; href?: string }> = [
  {
    date: 'Aug 4, 2026',
    title: 'Form 10-Q (period ended June 30, 2026)',
    detail: 'Note 3 customer-type revenue, Note 18 Connectivity, Key Business Metrics',
    href: 'https://www.sec.gov/Archives/edgar/data/1181412/000162828026052535/spcx-20260630.htm',
  },
  {
    date: 'Aug 4, 2026',
    title: 'Q2 2026 earnings release (EX-99.1)',
    detail: 'Connectivity $4,291 million; 12.0 million subscribers',
    href: 'https://www.sec.gov/Archives/edgar/data/1181412/000162828026052515/earningsreleaseq22608042.htm',
  },
  {
    date: 'May 20, 2026',
    title: 'Form S-1 / 424(b)(4)',
    detail: 'FY 2025 Connectivity $11,387 million; ARPU path; kit cost −59%; Mobile 7.4 million unique devices',
    href: 'https://www.sec.gov/Archives/edgar/data/1181412/000162828026036936/spaceexplorationtechnologi.htm',
  },
  {
    date: '2025',
    title: 'Starlink 2025 progress report',
    detail: 'Operational attach: vessels, aircraft, Direct-to-cell constellation — not Note 3 mix',
    href: 'https://starlink.com/public-files/starlinkProgressReport_2025.pdf',
  },
];

export function rowsFor(group: InvestorGroupId): InvestorRow[] {
  return INVESTOR_ROWS.filter((row) => row.group === group);
}

export function kindLabel(kind: InvestorKind): string {
  if (kind === 'printed') return '10-Q / S-1';
  if (kind === 'independent') return 'Independent';
  if (kind === 'company_report') return 'Company report';
  return 'Hole';
}
